import { useRef, useState } from 'react';
import { Eye, FileText, Paperclip, Upload, UserRound } from 'lucide-react';
import { formatFileSize, type PortalAssemblyDocument } from '@proerp/shared';
import { Badge, openFilePreview, Spinner } from '@proerp/ui';
import { usePortalDocumentUpload, usePortalDocuments } from '@/lib/usePortalDocuments';

/**
 * Unterlagen und Nachweise zu einem Einsatz (ADR 0087).
 *
 * **Aufklappbar, nicht dauerhaft offen:** Auf dem Zeitstrahl stehen viele
 * Einsätze; die Unterlagen interessieren immer nur bei einem. Erst beim
 * Aufklappen wird geladen — sonst stellte die Seite eine Abfrage je Einsatz,
 * für Antworten, die niemand liest.
 *
 * Der Rückkanal ist der eigentliche Gewinn: Fotos vom Fundament, der
 * unterschriebene Lieferschein, das Foto der beschädigten Kiste. Ohne ihn
 * kämen sie per WhatsApp und lägen nirgends am Vorgang.
 */
export function TaskDocuments({ taskId }: { taskId: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const documents = usePortalDocuments(taskId, { enabled: isOpen });
  const upload = usePortalDocumentUpload(taskId);
  const rows = documents.data ?? [];

  async function onPick(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    setError(null);
    try {
      await upload.mutateAsync(file);
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : 'Die Datei konnte nicht hochgeladen werden.',
      );
    } finally {
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  return (
    <div className="mt-3 border-t border-border pt-3">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <Paperclip className="size-4" aria-hidden />
        Unterlagen &amp; Nachweise
        <span aria-hidden>{isOpen ? '▾' : '▸'}</span>
      </button>

      {isOpen && (
        <div className="mt-3 flex flex-col gap-3">
          {documents.isPending && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="size-4" /> Unterlagen werden geladen …
            </p>
          )}
          {documents.isError && (
            <p className="text-sm text-danger-600">Unterlagen sind gerade nicht abrufbar.</p>
          )}

          {documents.data && rows.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Für diesen Einsatz liegen noch keine Unterlagen bereit. Du kannst hier Fotos und
              Nachweise hochladen.
            </p>
          )}

          {rows.length > 0 && (
            <ul className="flex flex-col gap-2">
              {rows.map((row) => (
                <li key={row.id}>
                  <DocumentRow taskId={taskId} document={row} />
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInput}
              type="file"
              className="sr-only"
              aria-label="Datei auswählen"
              onChange={(event) => void onPick(event.target.files)}
            />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={upload.isPending}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
            >
              {upload.isPending ? <Spinner className="size-4" /> : <Upload className="size-4" aria-hidden />}
              Foto oder Nachweis hochladen
            </button>
            <span className="text-xs text-muted-foreground">PDF, Bilder, Word, Excel · max. 50 MB</span>
          </div>

          {error && (
            <p role="alert" className="text-sm text-danger-600">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function DocumentRow({ taskId, document }: { taskId: string; document: PortalAssemblyDocument }) {
  /**
   * Ansehen statt herunterladen (ADR 0075): Auf der Baustelle ist ein Handy die
   * Regel — dort ist ein Download in einen unauffindbaren Ordner das Gegenteil
   * von „mal eben nachsehen". Herunterladen steht in der Vorschau.
   */
  function preview() {
    openFilePreview({
      path: `/assembly/${taskId}/documents/${document.id}/content`,
      title: document.caption ?? document.fileName,
      filename: document.fileName,
      mimeType: document.mimeType,
    });
  }

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2">
      <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 text-sm">
          <span className="truncate font-medium text-foreground">{document.fileName}</span>
          {document.source === 'PARTNER' && (
            <Badge tone="neutral">
              <UserRound className="size-3" aria-hidden />
              Von dir
            </Badge>
          )}
        </p>
        {document.caption && <p className="text-sm text-muted-foreground">{document.caption}</p>}
        <p className="text-xs text-muted-foreground">{formatFileSize(document.sizeBytes)}</p>
      </div>
      <button
        type="button"
        onClick={preview}
        className="inline-flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label={`${document.fileName} ansehen`}
      >
        <Eye className="size-4" aria-hidden />
        Ansehen
      </button>
    </div>
  );
}
